import styles from './DavidPifView.module.css'
import { useElementOutOfView } from '@/hooks/useElementOutOfView'
import { useIntersectionObserver } from '@/hooks/useIntersectionObserver'
import { TheatricalReleaseResult } from '@/services/Theatrical/queries'
import { Faq } from '@/types/codegen-federation'
import { ClaimTicketSection } from './components/ClaimTicketSection'
import { DavidGoliathSection } from './components/DavidGoliathSection'
import { EndCtaSection } from './components/EndCtaSection'
import { FaqSection } from './components/FaqSection'
import { HelpLightSection } from './components/HelpLightSection'
import { Hero } from './components/Hero'
import { HowItWorksSection } from './components/HowItWorksSection'
import { ImgFullParallax } from './components/ImgFullParallax'
import { InspireGenSection } from './components/InspireGenSection'
import { PifOverlay } from './components/PifOverlay'
import { ShareDavidCta } from './components/ShareDavidCta'
import { SpecialMessageSection } from './components/SpecialMessageSection'
import { StoriesSongsSection } from './components/StoriesSongsSection'
import { SuperheroesSection } from './components/SuperheroesSection'
import { PifOverlayProvider, usePifOverlay } from './contexts/PifOverlayContext'

interface DavidPifViewProps {
  theatricalRelease: TheatricalReleaseResult
  useChildLifeVideo?: boolean
  useTaliPopup?: boolean
  useDavidPifPanelV2?: boolean
}

function DavidPifViewContent({
  theatricalRelease,
  useChildLifeVideo,
  useTaliPopup = false,
  useDavidPifPanelV2 = false,
}: DavidPifViewProps) {
  const [heroRef, isHeroOutOfView] = useElementOutOfView<HTMLElement>()
  const [helpLightRef, hasReachedHelpLight] = useIntersectionObserver<HTMLElement>({
    rootMargin: '0px',
    triggerOnce: true,
  })
  const [faqRef, isFaqVisible] = useIntersectionObserver<HTMLElement>({
    rootMargin: '0px',
  })
  const { isOpen: isOverlayOpen } = usePifOverlay()
  const releaseDate = theatricalRelease?.region?.releaseDate

  const showShareCta = isHeroOutOfView && hasReachedHelpLight && !isOverlayOpen && !isFaqVisible && useTaliPopup

  return (
    <div className={`david-pif-view relative min-h-screen w-full overflow-x-hidden bg-[#141447] ${styles.container}`}>
      <ShareDavidCta show={showShareCta} />

      <main className="relative">
        <Hero ref={heroRef} useChildLifeVideo={useChildLifeVideo} />
        <HelpLightSection ref={helpLightRef} releaseDate={releaseDate} />
        <ClaimTicketSection />
        <InspireGenSection />
        <SpecialMessageSection useChildLifeVideo={useChildLifeVideo} />
        <SuperheroesSection />
        <StoriesSongsSection />
        <DavidGoliathSection />
        <HowItWorksSection />
        <EndCtaSection releaseDate={releaseDate} />
        <ImgFullParallax />
        <FaqSection ref={faqRef} faqs={theatricalRelease?.region?.faqs as Faq[]} />
      </main>

      <PifOverlay useDavidPifPanelV2={useDavidPifPanelV2} releaseDate={releaseDate} projectSlug="david" />
    </div>
  )
}

export function DavidPifView(props: DavidPifViewProps) {
  return (
    <PifOverlayProvider>
      <DavidPifViewContent {...props} />
    </PifOverlayProvider>
  )
}
